import React, { memo } from "react";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Typography,
} from "@mui/material";

const TONE_STYLES = {
  critical: {
    bar: "#ef4444",
    badgeBg: "#fef2f2",
    badgeColor: "#dc2626",
    personColor: "#dc2626",
    alertBg: "#fff",
  },
  warning: {
    bar: "#f59e0b",
    badgeBg: "#fffbeb",
    badgeColor: "#d97706",
    personColor: "#dc2626",
    alertBg: "#fff",
  },
};

const AttentionAlertItem = memo(({
  item,
  onFollowUp,
  onReassign,
  onPersonClick,
}) => {
  const tone = TONE_STYLES[item.tone] || TONE_STYLES.critical;

  return (
    <Alert
      className="dashboard-attention-alert"
      role="listitem"
      severity="error"
      icon={false}
      sx={{
        alignItems: "stretch",
        p: 0,
        borderRadius: 0,
        bgcolor: tone.alertBg,
        border: "none",
        borderBottom: "1px solid #f0f1f3",
        "&:last-child": { borderBottom: "none" },
        "& .MuiAlert-message": { p: 0, width: "100%" },
        "& .MuiAlert-action": { p: 0, m: 0, alignItems: "center" },
      }}
      action={
        <Box className="dashboard-attention-alert-actions">
          <Button
            variant="contained"
            size="small"
            className="dashboard-attention-btn dashboard-attention-btn--primary"
            onClick={() => onFollowUp?.(item)}
          >
            Follow Up
          </Button>
          <Button
            variant="outlined"
            size="small"
            className="dashboard-attention-btn dashboard-attention-btn--secondary"
            onClick={() => onReassign?.(item)}
          >
            Reassign
          </Button>
        </Box>
      }
    >
      <Box className="dashboard-attention-alert-body">
        <Box
          className="dashboard-attention-alert-bar"
          sx={{ bgcolor: tone.bar }}
          aria-hidden="true"
        />
        <Box className="dashboard-attention-alert-content">
          <Chip
            label={item.statusLabel}
            size="small"
            className="dashboard-attention-badge"
            sx={{
              bgcolor: tone.badgeBg,
              color: tone.badgeColor,
              fontWeight: 700,
              fontSize: "0.68rem",
              letterSpacing: "0.04em",
              height: 22,
              borderRadius: "4px",
              "& .MuiChip-label": { px: 1 },
            }}
          />
          <Typography className="dashboard-attention-alert-title" component="p">
            <button
              type="button"
              className="dashboard-attention-person-link"
              style={{ color: tone.personColor }}
              onClick={() => onPersonClick?.(item)}
            >
              {item.person}
            </button>
            {" — "}
            <span className="dashboard-attention-task">&quot;{item.task}&quot;</span>
            {" "}
            {item.message}
          </Typography>
          <Typography className="dashboard-attention-alert-meta" component="p">
            {item.metadata.join(" · ")}
          </Typography>
        </Box>
      </Box>
    </Alert>
  );
});

AttentionAlertItem.displayName = "AttentionAlertItem";

const DashboardAttentionAlerts = ({
  items = [],
  totalCount,
  onFollowUp,
  onReassign,
  onPersonClick,
  onShowMore,
}) => {
  if (!items.length) return null;

  const count = totalCount ?? items.length;

  return (
    <section className="dashboard-attention-section">
      <Box className="dashboard-attention-card">
        <Box className="dashboard-attention-header">
          <Box className="dashboard-attention-header-title">
            <WarningAmberOutlinedIcon className="dashboard-attention-header-icon" />
            <Typography component="h2" variant="subtitle1">
              Needs Your Attention
            </Typography>
          </Box>
          <Chip
            label={`${count} items`}
            size="small"
            className="dashboard-attention-count"
            sx={{
              bgcolor: "#fef2f2",
              color: "#dc2626",
              fontWeight: 600,
              fontSize: "0.75rem",
              height: 24,
              borderRadius: "999px",
            }}
          />
        </Box>

        <Box className="dashboard-attention-list" role="list">
          {items.map((item) => (
            <AttentionAlertItem
              key={item.id}
              item={item}
              onFollowUp={onFollowUp}
              onReassign={onReassign}
              onPersonClick={onPersonClick}
            />
          ))}
        </Box>

        {onShowMore && (
          <Box className="dashboard-attention-footer">
            <Button
              variant="text"
              className="dashboard-attention-show-more"
              onClick={onShowMore}
            >
              Show more items →
            </Button>
          </Box>
        )}
      </Box>
    </section>
  );
};

export default memo(DashboardAttentionAlerts);
